import DatePicker from '../DatePicker/DatePicker';
import List from '../List/List';
import styles from './Form.module.scss'
import clsx from "clsx";
import {useEffect} from "react";
import {useSelector} from "react-redux";
import dayjs from "dayjs";
import allowOnlyNumbers from "../../../../utilities/allowOnlyNumbers";

const Form = ({isEdited, formState, setFormState}) => {
    const invoice = useSelector((state) => state.invoices.currentInvoice);
    const invoiceErrors = useSelector((state) => state.errors.invoice);

    const getFieldError = (errors, field) => {
        if (!Array.isArray(errors?.msg)) return null;

        const fieldError = errors.msg.find((err) => err.err === field);

        if (fieldError && field === 'items') {
            const fieldErrors = errors.msg.filter((err) => err.err === field);
            return fieldErrors.flatMap((err) =>
                Array.isArray(err.msg) ? err.msg : [err.msg]
            );
        }

        return fieldError ? fieldError.msg : null;
    };

    const handleInvoiceChange = (event, type, date, index) => {
        const name = event?.target?.name;
        const value = event?.target?.value;

        switch (type) {
            case 'idenNumber':
                setFormState({...formState, number: value});
                break;
            case 'invoice':
                setFormState({...formState, [name]: value});
                break;
            case 'billingInfo': {
                setFormState({
                    ...formState,
                    billingInfo: {...formState.billingInfo, [name]: value},
                });
                break;
            }
            case 'paymentDue':
                setFormState({...formState, dueDate: value});
                break;
            case 'senderAddress': {
                setFormState({
                    ...formState,
                    sender: {
                        ...formState.sender,
                        address: {...formState.sender.address, [name]: value},
                    },
                });
                break;
            }
            case 'clientAddress': {
                setFormState({
                    ...formState,
                    customer: {
                        ...formState.customer,
                        address: {...formState.customer.address, [name]: value},
                    },
                });
                break;
            }
            case 'date': {
                const name = event?.target?.name;
                const value = event?.target?.value || dayjs(date).format();

                setFormState((prevState) => ({
                    ...prevState,
                    [name]: value,
                }));
                break;
            }
            case 'items':
                const newItems = formState.items.map((item, i) =>
                    i === index
                        ? {
                            ...item,
                            [name]: name === 'quantity' || name === 'price'
                                ? allowOnlyNumbers(value)
                                : value,
                            total: name === 'quantity' || name === 'price'
                                ? (name === 'quantity' ? allowOnlyNumbers(value) : item.quantity) *
                                (name === 'price' ? allowOnlyNumbers(value) : item.price)
                                : item.total,
                        }
                        : item
                );

                setFormState({
                    ...formState,
                    items: newItems,
                    total: newItems.reduce((acc, item) => acc + item.total, 0), // Recalculate total
                });
                break;
            default:
                console.error('No matching type for handleInvoiceChange:', type);
                break;
        }
    };

    useEffect(() => {
        if (isEdited && invoice) {
            setFormState(invoice);
        }
    }, []);

    useEffect(() => {
        console.log(invoiceErrors.err)
    }, []);

    return (
        <>
            {!isEdited && <h2 className={styles.title}>New Invoice</h2>}
            {isEdited && (
                <h2 className={styles.title}>
                    Edit <span className={styles.hashtag}># </span>
                    {invoice.number}
                </h2>
            )}
            <form className={styles.form}>
                <fieldset className={styles.fieldset}>
                    <legend className={styles.legend}>Invoice № {formState.number}</legend>
                    <div className={styles.inputContainer}>
                        <label
                            htmlFor="idenNumber"
                            className={clsx(styles.label, {
                                [styles.error]: getFieldError(invoiceErrors, 'idenNumber'),
                            })}
                        >
                            Number
                            {getFieldError(invoiceErrors, 'idenNumber') && (
                                <span className={styles.error}>{getFieldError(invoiceErrors, 'idenNumber')}</span>
                            )}
                        </label>
                        <input
                            className={clsx(styles.input, {
                                [styles.error]: getFieldError(invoiceErrors, 'idenNumber')
                            })}
                            type="text"
                            name="idenNumber"
                            placeholder="e.g. 00012-DEV-NS"
                            value={formState.number}
                            required
                            onChange={(event) =>
                                handleInvoiceChange(event, 'idenNumber')
                            }
                        />
                    </div>
                </fieldset>
                <fieldset className={styles.fieldset}>
                    <legend className={styles.legend}>Bill from</legend>
                    <div className={styles.inputContainer}>
                        <label
                            htmlFor="street"
                            className={clsx(styles.label, {
                                [styles.error]: invoiceErrors.senderAddress?.street,
                            })}
                        >
                            Street Address
                            {invoiceErrors.senderAddress?.street && (
                                <span className={styles.error}>can't be empty</span>
                            )}
                        </label>
                        <input
                            className={clsx(styles.input, {
                                [styles.error]: invoiceErrors.senderAddress?.street
                            })}
                            type="text"
                            name="street"
                            placeholder="e.g. 10A, Kosmichna st."
                            value={formState.sender.address.street}
                            onChange={(event) =>
                                handleInvoiceChange(event, 'senderAddress')
                            }
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <div className={styles.inputContainer}>
                            <label
                                htmlFor="city"
                                className={clsx(styles.label, {
                                    [styles.error]: invoiceErrors.senderAddress?.city,
                                })}
                            >
                                City
                                {invoiceErrors.senderAddress?.city && (
                                    <span className={styles.error}>can't be empty</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: invoiceErrors.senderAddress?.city,
                                })}
                                type="text"
                                name="city"
                                placeholder="e.g. Odesa"
                                value={formState.sender.address.city}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'senderAddress')
                                }
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                className={clsx(styles.label, {
                                    [styles.error]: invoiceErrors.senderAddress?.postCode,
                                })}
                                htmlFor="postCode"
                            >
                                Post Code
                                {invoiceErrors.senderAddress?.postCode && (
                                    <span className={styles.error}>can't be empty</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: invoiceErrors.senderAddress?.postCode,
                                })}
                                type="text"
                                name="postCode"
                                placeholder="e.g. 02045"
                                value={formState.sender.address.postCode}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'senderAddress')
                                }
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                className={clsx(styles.label, {
                                    [styles.error]: invoiceErrors.senderAddress?.country,
                                })}
                                htmlFor="country"
                            >
                                Country
                                {invoiceErrors.senderAddress?.country && (
                                    <span className={styles.error}>can't be empty</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: invoiceErrors.senderAddress?.country,
                                })}
                                type="text"
                                name="country"
                                placeholder="e.g. Ukraine"
                                value={formState.sender.address.country}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'senderAddress')
                                }
                            />
                        </div>
                    </div>
                </fieldset>
                <fieldset className={styles.fieldset}>
                    <legend className={styles.legend}>Bank information</legend>
                    <div className={styles.inputGroup}>
                        <div className={styles.inputContainer}>
                            <label
                                htmlFor="beneficiary"
                                className={clsx(styles.label, {
                                    [styles.error]: invoiceErrors.beneficiary,
                                })}
                            >
                                Beneficiary
                                {invoiceErrors.beneficiary && (
                                    <span className={styles.error}>can't be empty</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: invoiceErrors.beneficiary
                                })}
                                type="text"
                                name="beneficiary"
                                placeholder="e.g. John Doe"
                                value={formState.billingInfo.beneficiary}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'billingInfo')
                                }
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                className={clsx(styles.label, {
                                    [styles.error]: getFieldError(invoiceErrors, 'bicCode'),
                                })}
                                htmlFor="bicCode"
                            >
                                BIC code
                                {getFieldError(invoiceErrors, 'bicCode') && (
                                    <span className={styles.error}>{getFieldError(invoiceErrors, 'bicCode')}</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: getFieldError(invoiceErrors, 'bicCode'),
                                })}
                                type="text"
                                name="bicCode"
                                placeholder={'e.g. UBBUUAUKXXX'}
                                value={formState.billingInfo.bicCode}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'billingInfo')
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.inputGroup}>
                        <div className={styles.inputContainer}>
                            <label
                                className={clsx(styles.label, {
                                    [styles.error]: getFieldError(invoiceErrors, 'beneficiaryBank'),
                                })}
                                htmlFor="beneficiaryBank"
                            >
                                Beneficiary's Bank
                                {getFieldError(invoiceErrors, 'beneficiaryBank') && (
                                    <span className={styles.error}>{getFieldError(invoiceErrors, 'beneficiaryBank')}</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: getFieldError(invoiceErrors, 'beneficiaryBank'),
                                })}
                                type="text"
                                name="beneficiaryBank"
                                placeholder='e.g. UNIVERSAL BANK'
                                value={formState.billingInfo.beneficiaryBank}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'billingInfo')
                                }
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                className={clsx(styles.label, {
                                    [styles.error]: getFieldError(invoiceErrors, 'bankAddress'),
                                })}
                                htmlFor="bankAddress"
                            >
                                Bank Address
                                {getFieldError(invoiceErrors, 'bankAddress') && (
                                    <span className={styles.error}>{getFieldError(invoiceErrors, 'bankAddress')}</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: getFieldError(invoiceErrors, 'bankAddress'),
                                })}
                                type="text"
                                name="bankAddress"
                                placeholder='e.g. Kyiv, Ukraine'
                                value={formState.billingInfo.bankAddress}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'billingInfo')
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.inputContainer}>
                        <label
                            htmlFor="iban"
                            className={clsx(styles.label, {
                                [styles.error]: getFieldError(invoiceErrors, 'iban'),
                            })}
                        >
                            IBAN
                            {getFieldError(invoiceErrors, 'iban') && (
                                <span className={styles.error}>{getFieldError(invoiceErrors, 'iban')}</span>
                            )}
                        </label>
                        <input
                            className={clsx(styles.input, {
                                [styles.error]: getFieldError(invoiceErrors, 'iban'),
                            })}
                            type="text"
                            name="iban"
                            value={formState.billingInfo.iban}
                            placeholder="e.g. UA903052992990004149123456789"
                            onChange={(event) =>
                                handleInvoiceChange(event, 'billingInfo')
                            }
                        />
                    </div>
                </fieldset>
                <fieldset className={styles.fieldset}>
                    <div className={clsx(styles.inputGroup)}>
                        <div className={styles.inputContainer}>
                            <label className={styles.label}>Invoice Date</label>
                            <DatePicker handleInvoiceChange={handleInvoiceChange} value={formState.date}/>
                        </div>
                        <div className={styles.inputContainer}>
                            <label
                                htmlFor="paymentDue"
                                className={clsx(styles.label, {
                                    [styles.error]: getFieldError(invoiceErrors, 'paymentDue'),
                                })}
                            >
                                Payment Due
                                {getFieldError(invoiceErrors, 'paymentDue') && (
                                    <span className={styles.error}>{getFieldError(invoiceErrors, 'paymentDue')}</span>
                                )}
                            </label>
                            <input
                                className={clsx(styles.input, {
                                    [styles.error]: getFieldError(invoiceErrors, 'paymentDue')
                                })}
                                type="text"
                                name="paymentDue"
                                placeholder="e.g. December 2024"
                                value={formState.dueDate}
                                onChange={(event) =>
                                    handleInvoiceChange(event, 'paymentDue')
                                }
                            />
                        </div>
                    </div>
                </fieldset>
                <List handleInvoiceChange={handleInvoiceChange} formState={formState} setFormState={setFormState}/>
                {getFieldError(invoiceErrors, 'items') && (
                    <div className={styles.errorContainer}>
                        {Array.isArray(getFieldError(invoiceErrors, 'items')) ? (
                            getFieldError(invoiceErrors, 'items').map((itemError, index) => (
                                <span key={index} className={styles.error}>
                    {itemError}
                </span>
                            ))
                        ) : (
                            <span className={styles.error}>
                {getFieldError(invoiceErrors, 'items')}
            </span>
                        )}
                    </div>
                )}
            </form>
        </>
    );
};

export default Form;
